import { useContext, useMemo, useRef, useState, type FC } from 'react';
import { Loading } from 'src/components/shared/elements/loading';
import { DEFAULT_CATEGORY_ID, useBrandList } from 'src/hooks/useBrandList';
import { useResponsive } from 'src/hooks/useResponsive';
import { I18nContext } from 'src/stores/I18n';
import type { BrandsMap } from 'src/types/brand';
import { smoothScrollTo } from 'src/utils/scrollToElement';
import { Presenter } from './Presenter';

export const HEADER_HEIGHT = 94;
export const FOOTER_IMAGE_LIST = [
  { path: '/common/footer/japan-tax-free.webp', width: 120, height: 54 },
  { path: '/common/footer/privacy-security.webp', width: 162, height: 62 },
  { path: '/common/footer/product-authentication.webp', width: 148, height: 62 }
];

type Props = {
  headingLevel: 'h2' | 'h3';
};

export const AllBrands: FC<Props> = ({ headingLevel }) => {
  const { data, isLoading, isError } = useBrandList('', DEFAULT_CATEGORY_ID);
  const [activeIndex, setActiveIndex] = useState<string | null>(null);
  const [isSeeMoreButtonExpanded, setIsSeeMoreButtonExpanded] = useState<boolean>(false);
  const listEndRef = useRef<HTMLDivElement | null>(null);
  const initialEndRef = useRef<HTMLDivElement | null>(null);
  const Heading = headingLevel === 'h2' ? 'h2' : 'h3';
  const { isDesktop } = useResponsive();
  const INITIAL_VISIBLE_COUNT = isDesktop ? 24 : 12;
  const localeResources = useContext(I18nContext);

  const visibleCount = useMemo(() => {
    if (isSeeMoreButtonExpanded) {
      if (activeIndex !== null) {
        const extractedData = extractDataByIndex(data, activeIndex);
        return extractedData[activeIndex]?.length || INITIAL_VISIBLE_COUNT;
      }
      return INITIAL_VISIBLE_COUNT;
    }
    return INITIAL_VISIBLE_COUNT;
  }, [isSeeMoreButtonExpanded, activeIndex, data, INITIAL_VISIBLE_COUNT]);

  const handleToggleClick = () => {
    setIsSeeMoreButtonExpanded(!isSeeMoreButtonExpanded);
    if (isSeeMoreButtonExpanded) {
      const targetY = initialEndRef.current?.getBoundingClientRect().top! + window.scrollY - HEADER_HEIGHT;
      smoothScrollTo(targetY, 500);
    } else {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClickBrandList = (brandIndex: string) => {
    const targetY = initialEndRef.current?.getBoundingClientRect().top! + window.scrollY - HEADER_HEIGHT;
    smoothScrollTo(targetY, 500);
    setActiveIndex(brandIndex);
    setIsSeeMoreButtonExpanded(false);
  };

  if (isLoading) return <Loading />;
  if (isError) return null;

  return (
    <Presenter
      handleToggleClick={handleToggleClick}
      handleClickBrandList={handleClickBrandList}
      initialVisibleCount={INITIAL_VISIBLE_COUNT}
      initialEndRef={initialEndRef}
      listEndRef={listEndRef}
      Heading={Heading}
      activeIndex={activeIndex}
      visibleCount={visibleCount}
      isSeeMoreButtonExpanded={isSeeMoreButtonExpanded}
      footerImageList={FOOTER_IMAGE_LIST}
      headingTitle={localeResources.brands.list.title}
      data={data}
    />
  );
};

export const extractDataByIndex = (data: BrandsMap[], index: string) => data.find((current) => current[index]) ?? {};
